import { AnalyticsEvent, PlatformType } from '@cibli/shared';
import * as Sentry from '@sentry/react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useCVTemplateContext } from '@/features/common/contexts/cv-template.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { INTERVIEW_STEPS } from '@/features/interview/contexts/interview-flow.context';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { createEvent, updateEventSession } from '@/services/analytics';

interface TrackingContextState {
  eventSessionId: string | null;
  initSession: () => void;
  resetSession: (interviewStep?: INTERVIEW_STEPS) => void;
  cancelSession: () => void;
}

const TrackingContext = createContext<TrackingContextState | undefined>(undefined);

interface TrackingProviderProps {
  children: ReactNode;
}

export function TrackingProvider({ children }: TrackingProviderProps) {
  const { booth, platformType } = useBooth();
  const { user } = useUserContext();
  const { currentLocaleCv } = useCVTemplateContext();

  const [eventSessionId, setEventSessionId] = useState<string | null>(null);
  const [sessionBoothId, setSessionBoothId] = useState<string | undefined>();

  // Prevents rapid async double-create when booth churns during boot.
  // Intentionally NOT reset by resetSession — next session comes from full page reload.
  const initRef = useRef(false);

  const initSession = useCallback(async () => {
    if (eventSessionId || initRef.current) return;
    if (!platformType) return;
    initRef.current = true;

    const maxRetries = 3;
    const backoffMs = [1000, 2000, 4000];

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const event = await createEvent({
          name: AnalyticsEvent.SESSION_STARTED,
          boothId: booth?.id,
          platformType,
          userId: user?.id,
        });
        setEventSessionId(event.eventSessionId);
        setSessionBoothId(booth?.id);

        if (platformType === PlatformType.Web) {
          Sentry.setTag('webSessionId', event.eventSessionId);
        }

        return;
      } catch (error) {
        console.error(
          `Failed to initialize analytics session (attempt ${attempt + 1}/${maxRetries}):`,
          error,
        );
        if (attempt < maxRetries - 1) {
          await new Promise(r => setTimeout(r, backoffMs[attempt]));
        }
      }
    }

    initRef.current = false;
  }, [booth?.id, platformType, eventSessionId, user?.id]);

  useEffect(() => {
    initSession();
  }, [initSession]);

  const getEventName = useCallback((interviewStep?: INTERVIEW_STEPS) => {
    if (interviewStep === INTERVIEW_STEPS.SUMMARY) {
      return AnalyticsEvent.SESSION_ENDED;
    }
    return AnalyticsEvent.SESSION_CANCELLED;
  }, []);

  const resetSession = useCallback(
    async (interviewStep?: INTERVIEW_STEPS) => {
      const eventName = getEventName(interviewStep);
      if (eventSessionId) {
        try {
          await createEvent({
            name: eventName,
            eventSessionId,
            platformType: platformType ?? undefined,
            boothId: sessionBoothId,
            userId: user?.id,
          });
        } catch (error) {
          console.error('Failed to end analytics session:', error);
        }
      }

      setEventSessionId(null);
    },
    [getEventName, eventSessionId, platformType, sessionBoothId, user?.id],
  );

  const cancelSession = useCallback(async () => {
    if (eventSessionId) {
      try {
        await createEvent({
          name: AnalyticsEvent.SESSION_CANCELLED,
          eventSessionId,
          platformType: platformType ?? undefined,
          boothId: sessionBoothId,
          userId: user?.id,
        });
      } catch (error) {
        console.error('Failed to cancel analytics session:', error);
      }
    }
  }, [eventSessionId, platformType, sessionBoothId, user?.id]);

  useEffect(() => {
    if (!eventSessionId) return;
    (async () => {
      try {
        await updateEventSession(eventSessionId, { language: currentLocaleCv });
      } catch (error) {
        console.error('Failed to update language in EventSession:', error);
      }
    })();
  }, [eventSessionId, currentLocaleCv]);

  const value: TrackingContextState = {
    eventSessionId,
    initSession,
    resetSession,
    cancelSession,
  };

  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTrackingContext(): TrackingContextState {
  const context = useContext(TrackingContext);
  if (context === undefined) {
    throw new Error('useTrackingContext must be used within a TrackingProvider');
  }
  return context;
}
